// Which bucket each file of a job goes to.
//
// The browser sends a bucket's parts over one connection, and one connection's speed falls with the
// distance to the bucket: from San Francisco a New York bucket is capped at a fraction of the
// contributor's line. So when the job has nearby upload destinations (ticked in pm), the page picks
// the fastest for this contributor. The file stays where it lands; the backend records it, and
// previews, exports and delivery all read each file from its own bucket.
//
// Decided up front, when the page opens: a few MB are uploaded to each bucket in turn, which takes
// seconds and happens while the first files are still being hashed. Timing a tiny request instead
// picked a bucket that then uploaded at a quarter of the speed of the one it passed over, so this is
// a real upload. After that, the real parts keep each bucket's speed current: a new file goes
// elsewhere only once its bucket has become slower than another, and a file already started moves
// only in its first 2% and only to a bucket at least 3x faster, so a move can never throw away more
// than a few tens of MB.
//
// null means "the job's own bucket", which is also the answer whenever anything here fails: this is
// only ever an optimisation, never a reason for an upload not to start.

const ENDPOINT = process.env.REACT_APP_ENDPOINT;

// The speed test: an untimed warm-up (DNS, TLS, the CORS preflight), then a timed upload, cut short
// after SPEED_TEST_MAX_MS on a slow bucket and measured from what was sent by then.
//
// Both are sent as SPEED_TEST_STREAMS uploads at once, as the /speedtest page does, because that is
// how a real file travels: several parts in flight. It matters most for AWS and R2, which speak
// HTTP/1.1 to the browser, so every part in flight is a connection of its own. Tested with a single
// upload, the S3 accelerated bucket measured 2.9 MB/s where /speedtest measured 36. DigitalOcean
// speaks HTTP/2, so its parts share one connection whichever way it is tested.
const WARM_UP_BYTES = 256 * 1024;
const SPEED_TEST_BYTES = 8 * 1024 * 1024;
const SPEED_TEST_STREAMS = 3;
const SPEED_TEST_MAX_MS = 3000;

// One test is a few seconds of someone else's line, and a dip in it (another tab, the first files
// being read from disk) is enough to rank an accelerated S3 bucket below a DigitalOcean one it beats
// by far on /speedtest. So the buckets that came within this factor of the fastest are tested again,
// in reverse order so that no bucket is always the one tested while the page is still opening, and
// each keeps its better run: a dip can only make a bucket look slower, never faster.
const RETEST_WITHIN = 2;

// Parts smaller than this are mostly round trip, not transfer, so they say nothing about speed.
const MIN_SAMPLE_BYTES = 4 * 1024 * 1024;
// How much each finished part moves its bucket's speed: enough to follow a slowdown within a few.
const SMOOTHING = 0.3;
const MIN_LIVE_SAMPLES = 2;
const MOVE_BEFORE_FRACTION = 0.02;
const MOVE_WHEN_FASTER_BY = 3;

// Per job, the speed test (and with it the buckets on offer). Per bucket, its speeds in bytes/ms:
// shared by every job on the page, since a bucket's distance from the contributor does not depend
// on the job.
const tests = new Map();
const tested = new Map();
const live = new Map(); // storageTargetId -> { bytesPerMs, samples }
const inFlight = new Map();

async function fetchTargets(crowdJobId) {
    const response = await fetch(`${ENDPOINT}/files/upload-targets?crowdJobId=${encodeURIComponent(crowdJobId)}`,
        { credentials: "include" });
    if (!response.ok) {
        throw new Error(`status ${response.status}`);
    }
    const { targets = [] } = await response.json();
    return targets;
}

// Random bytes, as /speedtest sends, so nothing on the way can shrink the test by compressing it.
// getRandomValues fills at most 64 KB a call, so one random block is repeated. Zeros where there is
// no crypto: the test must never be the reason an upload does not start.
const RANDOM_BLOCK_BYTES = 65536;
let randomBlock = null;

function payload(bytes) {
    if (!randomBlock) {
        randomBlock = new Uint8Array(RANDOM_BLOCK_BYTES);
        if (window.crypto?.getRandomValues) {
            window.crypto.getRandomValues(randomBlock);
        }
    }
    const blocks = [];
    for (let remaining = bytes; remaining > 0; remaining -= RANDOM_BLOCK_BYTES) {
        blocks.push(remaining >= RANDOM_BLOCK_BYTES ? randomBlock : randomBlock.subarray(0, remaining));
    }
    return new Blob(blocks, { type: "text/plain" });
}

// One upload of `bytes` to `url`: how much of it was sent, over which window, and whether it worked.
// A slow upload is cut short at maxMs and counts what it had sent by then.
function timedPut(url, bytes, maxMs) {
    return new Promise(resolve => {
        const xhr = new XMLHttpRequest();
        let sent = 0;
        let startedAt = null;
        let progressed = false;
        const finish = (ok) => {
            clearTimeout(timer);
            resolve({ ok: ok && startedAt !== null, sent, startedAt, endedAt: performance.now() });
        };
        const timer = setTimeout(() => {
            xhr.onload = xhr.onerror = null;
            xhr.abort();
            finish(true);
        }, maxMs);

        xhr.open("PUT", url);
        // loadstart fires when the request is queued, before its preflight, so the clock restarts at
        // the first progress event, from the bytes sent by then: a far bucket's extra round trip
        // before the body says nothing about how fast the body goes.
        let skipped = 0;
        xhr.upload.onloadstart = () => { startedAt = performance.now(); };
        xhr.upload.onprogress = (e) => {
            if (!progressed) {
                progressed = true;
                startedAt = performance.now();
                skipped = e.loaded;
            }
            sent = e.loaded - skipped;
        };
        xhr.onload = () => {
            sent = bytes - skipped;
            finish(xhr.status >= 200 && xhr.status < 300);
        };
        xhr.onerror = () => finish(false);
        xhr.send(payload(bytes));
    });
}

// Bytes per ms for SPEED_TEST_STREAMS uploads of `bytes` sent at once: everything sent, over the time
// from the first byte out to the last upload ending. 0 if any of them failed, since a bucket that
// refuses some uploads will refuse some parts too.
async function timedPuts(url, bytes, maxMs) {
    const puts = await Promise.all(Array.from({ length: SPEED_TEST_STREAMS }, () => timedPut(url, bytes, maxMs)));
    if (!puts.every(put => put.ok)) {
        return 0;
    }
    const elapsed = Math.max(...puts.map(put => put.endedAt)) - Math.min(...puts.map(put => put.startedAt));
    const sent = puts.reduce((total, put) => total + put.sent, 0);
    return elapsed > 0 ? sent / elapsed : 0;
}

// Which bucket a log line is about. Several can share a region, so the region alone is not enough.
function describe(target) {
    try {
        return `${target.region} (${new URL(target.speedTestUrl).host})`;
    }
    catch (e) {
        return target.region;
    }
}

// One bucket at a time, so the tests do not compete with each other for the line.
async function speedTest(crowdJobId) {
    let targets;
    try {
        targets = await fetchTargets(crowdJobId);
    }
    catch (e) {
        console.warn("Could not list the upload destinations; using the job's own bucket.", e);
        return [];
    }
    if (targets.length < 2) {
        return targets;
    }

    const untested = targets.filter(target => !tested.has(target.id));
    for (const target of untested) {
        await testOne(target);
    }
    const best = Math.max(...untested.map(target => tested.get(target.id)));
    const close = untested.filter(target => tested.get(target.id) > 0 && tested.get(target.id) * RETEST_WITHIN >= best);
    if (close.length > 1) {
        for (const target of close.reverse()) {
            await testOne(target);
        }
    }
    return targets;
}

async function testOne(target) {
    await timedPuts(target.speedTestUrl, WARM_UP_BYTES, SPEED_TEST_MAX_MS);
    const bytesPerMs = await timedPuts(target.speedTestUrl, SPEED_TEST_BYTES, SPEED_TEST_MAX_MS);
    const previous = tested.get(target.id);
    tested.set(target.id, previous === undefined ? bytesPerMs : Math.max(previous, bytesPerMs));
    console.info(`[upload] speed test ${describe(target)}: ${mbps(bytesPerMs)}`
        + (previous === undefined ? "" : ` (earlier ${mbps(previous)})`));
}

// Starts the speed test for a job. Called when the page opens, so it is done before the first file
// needs it; any upload asking for a bucket waits for the same test rather than starting another.
export function testUploadSpeeds(crowdJobId) {
    if (!crowdJobId) {
        return Promise.resolve([]);
    }
    if (!tests.has(crowdJobId)) {
        tests.set(crowdJobId, speedTest(crowdJobId));
    }
    return tests.get(crowdJobId);
}

function mbps(bytesPerMs) {
    return `${(bytesPerMs * 1000 / 1048576).toFixed(1)} MB/s`;
}

// What a bucket is doing now if its parts say so, otherwise what its speed test said.
function speedOf(id) {
    const current = live.get(id);
    return current && current.samples >= MIN_LIVE_SAMPLES ? current.bytesPerMs : (tested.get(id) || 0);
}

function fastest(targets) {
    return [...targets].sort((a, b) => speedOf(b.id) - speedOf(a.id))[0];
}

// The bucket a new file of this job starts in.
export default async function chooseUploadTarget(crowdJobId) {
    const targets = await testUploadSpeeds(crowdJobId);
    if (targets.length < 2) {
        return null;
    }
    const best = fastest(targets);
    if (!(speedOf(best.id) > 0)) {
        return null; // nothing answered the test
    }
    console.info(`[upload] using ${describe(best)} at ${mbps(speedOf(best.id))}`);
    return best.id;
}

// A part is being sent to this bucket. Returns what to call once it has landed, with its size.
//
// The bucket's speed is the part's own speed times the parts it shared the connection with, so that
// it is the whole connection's throughput: what the speed test measured, and what a file moved
// there would share in.
export function startPart(storageTargetId) {
    if (!storageTargetId) {
        return () => {};
    }
    inFlight.set(storageTargetId, (inFlight.get(storageTargetId) || 0) + 1);
    const sentAt = performance.now();

    return (bytes) => {
        const sharing = inFlight.get(storageTargetId);
        inFlight.set(storageTargetId, sharing - 1);
        const ms = performance.now() - sentAt;
        if (!(bytes >= MIN_SAMPLE_BYTES) || !(ms > 0)) {
            return;
        }
        const sample = bytes / ms * sharing;
        const previous = live.get(storageTargetId);
        live.set(storageTargetId, {
            bytesPerMs: previous ? previous.bytesPerMs + SMOOTHING * (sample - previous.bytesPerMs) : sample,
            samples: (previous?.samples || 0) + 1,
        });
    };
}

// A bucket to move a file to, or null to keep it where it is.
export async function betterTargetFor(crowdJobId, storageTargetId, fractionDone) {
    if (!crowdJobId || !storageTargetId || fractionDone > MOVE_BEFORE_FRACTION) {
        return null;
    }
    const targets = await testUploadSpeeds(crowdJobId);
    const current = live.get(storageTargetId);
    if (targets.length < 2 || !current || current.samples < MIN_LIVE_SAMPLES) {
        return null;
    }
    const best = fastest(targets.filter(target => target.id !== storageTargetId));
    if (speedOf(best.id) < MOVE_WHEN_FASTER_BY * current.bytesPerMs) {
        return null;
    }
    const from = targets.find(target => target.id === storageTargetId)?.region || storageTargetId;
    console.info(`[upload] moving a file from ${from} (${mbps(current.bytesPerMs)}) to ${best.region} (${mbps(speedOf(best.id))})`);
    return best.id;
}

// Test seam: the speeds are module state shared by the whole page.
export function __resetUploadTargetsForTests() {
    tests.clear();
    tested.clear();
    live.clear();
    inFlight.clear();
}
